import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getTiktokWorkspaceToken,
  persistTiktokWorkspaceToken,
  setTiktokWorkspaceAuthRequiredHandler,
  tiktokPlatformAPI,
} from '@/tiktok-platform/api/tiktokPlatformApi';
import {
  IPE_TIKTOK_GATE_PATH,
  IPE_TIKTOK_BASE_PATH,
  TIKTOK_WORKSPACE_SHOP_TYPE_KEY,
  normalizeTiktokShopType,
  parseTiktokShopTypeSlugFromPath,
  shopTypeSlugForLabel,
  normalizeTiktokShopTypeSlug,
} from '@/tiktok-platform/constants/tiktokRoutes';
import { allowDocumentUnload } from '@/tiktok-platform/hooks/allowDocumentUnload';
import { installWorkspaceShopTypeRedirect } from '@/utils/workspaceShopTypeRedirect';

const TiktokWorkspaceAuthContext = createContext(null);

function readStoredShopType() {
  try {
    return localStorage.getItem(TIKTOK_WORKSPACE_SHOP_TYPE_KEY) || '';
  } catch {
    return '';
  }
}

function persistShopType(shopType) {
  try {
    const normalized = normalizeTiktokShopType(shopType);
    if (normalized) {
      localStorage.setItem(TIKTOK_WORKSPACE_SHOP_TYPE_KEY, normalized);
    } else {
      localStorage.removeItem(TIKTOK_WORKSPACE_SHOP_TYPE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function TiktokWorkspaceAuthProvider({ children }) {
  const [token, setToken] = useState(() => getTiktokWorkspaceToken());
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [expiresAt, setExpiresAt] = useState(null);
  const [workspaceShopType, setWorkspaceShopType] = useState(() => readStoredShopType());

  const clearLocalSession = useCallback(() => {
    persistTiktokWorkspaceToken(null);
    persistShopType('');
    setToken(null);
    setUnlocked(false);
    setExpiresAt(null);
    setWorkspaceShopType('');
  }, []);

  const refreshStatus = useCallback(async () => {
    if (!token) {
      setUnlocked(false);
      setExpiresAt(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    try {
      const status = await tiktokPlatformAPI.getSessionStatus();
      setUnlocked(Boolean(status?.unlocked));
      setExpiresAt(status?.expiresAt || null);
      const type = normalizeTiktokShopType(status?.shopType) || readStoredShopType();
      if (type) {
        setWorkspaceShopType(type);
        persistShopType(type);
      }
    } catch {
      setUnlocked(false);
      setExpiresAt(null);
      persistTiktokWorkspaceToken(null);
      setToken(null);
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => installWorkspaceShopTypeRedirect({
    storageKey: TIKTOK_WORKSPACE_SHOP_TYPE_KEY,
    basePath: IPE_TIKTOK_BASE_PATH,
    parseSlugFromPath: parseTiktokShopTypeSlugFromPath,
    shopTypeToSlug: (raw) => shopTypeSlugForLabel(raw) || normalizeTiktokShopTypeSlug(raw),
  }), []);

  useEffect(() => {
    setTiktokWorkspaceAuthRequiredHandler(() => {
      clearLocalSession();
      if (typeof window === 'undefined') return;
      const path = window.location.pathname || '';
      if (path.includes('/gate')) return;
      const redirect = `${path}${window.location.search || ''}`;
      const q = redirect && redirect !== IPE_TIKTOK_GATE_PATH
        ? `?redirect=${encodeURIComponent(redirect)}`
        : '';
      allowDocumentUnload();
      window.location.replace(`${IPE_TIKTOK_GATE_PATH}${q}`);
    });
    return () => setTiktokWorkspaceAuthRequiredHandler(null);
  }, [clearLocalSession]);

  const unlock = useCallback(async (password, shopType) => {
    const normalizedType = normalizeTiktokShopType(shopType) || '';
    const res = await tiktokPlatformAPI.unlockSession(password, normalizedType || undefined);
    const nextToken = res?.token;
    if (!nextToken) {
      throw new Error('验证失败');
    }
    const boundType = normalizeTiktokShopType(res?.shopType) || normalizedType;
    persistTiktokWorkspaceToken(nextToken);
    persistShopType(boundType);
    setToken(nextToken);
    setUnlocked(true);
    setExpiresAt(res?.expiresAt || null);
    setWorkspaceShopType(boundType);
    return res;
  }, []);

  const lock = useCallback(async () => {
    try {
      await tiktokPlatformAPI.lockSession();
    } catch {
      /* ignore */
    }
    clearLocalSession();
  }, [clearLocalSession]);

  const value = useMemo(
    () => ({
      unlocked,
      checking,
      expiresAt,
      workspaceShopType,
      unlock,
      lock,
      refreshStatus,
    }),
    [unlocked, checking, expiresAt, workspaceShopType, unlock, lock, refreshStatus],
  );

  return (
    <TiktokWorkspaceAuthContext.Provider value={value}>
      {children}
    </TiktokWorkspaceAuthContext.Provider>
  );
}

export function useTiktokWorkspaceAuth() {
  const ctx = useContext(TiktokWorkspaceAuthContext);
  if (!ctx) {
    throw new Error('useTiktokWorkspaceAuth must be used within TiktokWorkspaceAuthProvider');
  }
  return ctx;
}

export function useTiktokWorkspaceAuthOptional() {
  return useContext(TiktokWorkspaceAuthContext);
}
